import _ from 'lodash'
import { useMemo, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import {
    useGetActsQuery,
    useCreateActMutation,
    useLazyGetActsQuery,
    useUpdateActMutation,
    useDeleteActMutation,
} from '@/core/services/acts'

import DesmosActs, { DesmosActsHandle, DesmosActData } from '../components/DesmosActs'

function DesmosActsContainer() {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    const desmosActsRef = useRef<DesmosActsHandle>(null)

    const { data: acts = [], isLoading: isListing } = useGetActsQuery({})
    const [getActs] = useLazyGetActsQuery()
    const [createAct, { isLoading: isCreating }] = useCreateActMutation()
    const [updateAct, { isLoading: isUpdating }] = useUpdateActMutation()
    const [deleteAct, { isLoading: isDeleting }] = useDeleteActMutation()

    const actsData = useMemo(() => _.map(acts, (act) => ({
        id: act._id,
        honoraire: act.honoraire,
        id_transposition: act.id_transposition,
        libelle: act.libelle
    })), [acts])

    const handleCreate = async (values: Partial<DesmosActData>) => {
        if (values.honoraire && values.id_transposition && values.libelle) {
            try {
                await createAct({
                    honoraire: values.honoraire,
                    id_transposition: values.id_transposition,
                    libelle: values.libelle,
                }).unwrap()
                enqueueSnackbar(t('notification.act.create.success'), { variant: 'success' })
                desmosActsRef.current?.toggleOptionFormDialog()
            } catch (error) {
                enqueueSnackbar(t('notification.act.create.error'), { variant: 'error' })
            }
        }
    }

    const handleUpdate = async (id: string, values: Partial<DesmosActData>) => {
        try {
            await updateAct({
                id,
                changes: {
                    ...(values.honoraire !== undefined && { honoraire: values.honoraire }),
                    ...(values.id_transposition !== undefined && { id_transposition: values.id_transposition }),
                    ...(values.libelle !== undefined && { libelle: values.libelle }),
                },
            }).unwrap()
            enqueueSnackbar(
                t('notification.act.update.success', { defaultValue: 'Acte mis à jour' }),
                { variant: 'success' },
            )
            desmosActsRef.current?.toggleOptionFormDialog()
        } catch (error) {
            enqueueSnackbar(
                t('notification.act.update.error', { defaultValue: 'Échec de la mise à jour de l\'acte' }),
                { variant: 'error' },
            )
        }
    }

    const handleDelete = async (id: string) => {
        try {
            await deleteAct(id).unwrap()
            enqueueSnackbar(
                t('notification.act.delete.success', { defaultValue: 'Acte supprimé' }),
                { variant: 'success' },
            )
            desmosActsRef.current?.toggleDeleteOptionDialog()
        } catch (error) {
            enqueueSnackbar(
                t('notification.act.delete.error', { defaultValue: 'Échec de la suppression de l\'acte' }),
                { variant: 'error' },
            )
        }
    }

    const handleSearch = (text: string) => {
        getActs({ smartSearch: text })
    }

    return (
        <DesmosActs
            ref={desmosActsRef}
            data={actsData}
            isCreating={isCreating}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
            isListing={isListing}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onSearch={handleSearch}
        />
    )
}

export default DesmosActsContainer